import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress
} from '@mui/material';
import {
  Publish as PublishIcon,
  CheckCircle as ApproveIcon,
  PlayForWork as ReleaseIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { publish, approve, release, archive, deleteCollection } from '../../services/practice-collection-service';

const CollectionActionButtons = ({
  collectionId,
  status,
  onActionComplete,
  size = 'small',
  showText = false,
  sx = {}
}) => {
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'publish' | 'approve' | 'release' | 'archive' | 'delete'

  const handleActionClick = (e, action) => {
    e.stopPropagation();
    setPendingAction(action);
    setConfirmOpen(true);
  };

  const handleCloseConfirm = (e) => {
    if (e) e.stopPropagation();
    setConfirmOpen(false);
    setPendingAction(null);
  };

  const handleConfirmAction = async (e) => {
    if (e) e.stopPropagation();
    setConfirmOpen(false);
    setLoading(true);

    try {
      if (pendingAction === 'publish') {
        await publish(collectionId);
      } else if (pendingAction === 'approve') {
        await approve(collectionId);
      } else if (pendingAction === 'release') {
        await release(collectionId);
      } else if (pendingAction === 'archive') {
        await archive(collectionId);
      } else if (pendingAction === 'delete') {
        await deleteCollection(collectionId);
      }
      
      if (onActionComplete) {
        onActionComplete(pendingAction);
      }
    } catch (error) {
      console.error(`Failed to execute ${pendingAction} action:`, error);
      alert(error.message || `Failed to execute ${pendingAction}`);
    } finally {
      setLoading(false);
      setPendingAction(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', p: 1, ...sx }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  // Render buttons based on status
  const canPublish = status === 'DRAFT';
  const canApprove = status === 'PUBLISHED';
  const canRelease = status === 'APPROVED';
  const canArchive = status === 'PUBLISHED' || status === 'APPROVED' || status === 'RELEASED';
  const canDelete = status === 'DRAFT';

  if (!canPublish && !canApprove && !canRelease && !canArchive && !canDelete) {
    return null;
  }

  const getActionTitle = () => {
    switch (pendingAction) {
      case 'publish': return 'Publish Collection';
      case 'approve': return 'Approve Collection';
      case 'release': return 'Release Collection';
      case 'archive': return 'Archive Collection';
      case 'delete': return 'Delete Collection';
      default: return '';
    }
  };

  const getActionMessage = () => {
    switch (pendingAction) {
      case 'publish':
        return 'Are you sure you want to publish this practice collection? This will make it visible for publishing / review.';
      case 'approve':
        return 'Are you sure you want to approve this practice collection? This confirms it is ready to be released to users.';
      case 'release':
        return 'Are you sure you want to release this practice collection? This will make it active and available to users.';
      case 'archive':
        return 'Are you sure you want to archive this practice collection? This action is typically used for deprecation.';
      case 'delete':
        return 'Are you sure you want to delete this practice collection? This will permanently delete the collection and all its generated reading practice drafts. This action cannot be undone.';
      default:
        return '';
    }
  };

  const renderButton = (action, icon, label, color) => {
    if (showText) {
      return (
        <Button
          variant="outlined"
          color={color}
          size={size === 'small' ? 'small' : 'medium'}
          startIcon={icon}
          onClick={(e) => handleActionClick(e, action)}
          sx={{ mr: 1 }}
        >
          {label}
        </Button>
      );
    }

    return (
      <Tooltip title={label} placement="top">
        <IconButton
          color={color}
          size={size}
          onClick={(e) => handleActionClick(e, action)}
          sx={{
            border: '1px solid',
            borderColor: `${color}.light`,
            mr: 1,
            '&:hover': {
              backgroundColor: `${color}.lighter`
            }
          }}
        >
          {icon}
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', ...sx }} onClick={(e) => e.stopPropagation()}>
      {canPublish && renderButton('publish', <PublishIcon fontSize="small" />, 'Publish', 'primary')}
      {canApprove && renderButton('approve', <ApproveIcon fontSize="small" />, 'Approve', 'info')}
      {canRelease && renderButton('release', <ReleaseIcon fontSize="small" />, 'Release', 'success')}
      {canArchive && renderButton('archive', <ArchiveIcon fontSize="small" />, 'Archive', 'warning')}
      {canDelete && renderButton('delete', <DeleteIcon fontSize="small" />, 'Delete', 'error')}

      <Dialog
        open={confirmOpen}
        onClose={handleCloseConfirm}
        onClick={(e) => e.stopPropagation()}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          {getActionTitle()}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            {getActionMessage()}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmAction} color="primary" autoFocus variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CollectionActionButtons;
